import { useContext } from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import { MainContext } from "../../../../main/context/MainContextProvider";

export type ProgressoAnualCardProps = {
    realYTD: number;
    metaAnual: number;
    metaYTD: number;
};

export default function ProgressoAnualCard({
    realYTD,
    metaAnual,
    metaYTD
}: ProgressoAnualCardProps) {
    const { theme } = useContext(MainContext);

    // Progresso real e esperado
    const progressPercentage = (realYTD / metaAnual) * 100;
    const expectedProgressPercentage = (metaYTD / metaAnual) * 100;
    
    // Desvio entre real e esperado
    const deviationPercentage = progressPercentage - expectedProgressPercentage;
    
    // Regras de cores baseadas no desvio
    const getBarColor = () => {
        if (progressPercentage > expectedProgressPercentage) {
            return "#10B981"; // Verde: acima do esperado
        } else if (progressPercentage >= expectedProgressPercentage * 0.95) {
            return "#F59E0B"; // Amarelo: até 5% abaixo do esperado
        } else {
            return "#EF4444"; // Vermelho: mais de 5% abaixo do esperado
        }
    };

    const barColor = getBarColor();

    const getBackgroundColor = () => {
        if (progressPercentage > expectedProgressPercentage) {
            return "rgba(16, 185, 129, 0.1)";
        } else if (progressPercentage >= expectedProgressPercentage * 0.95) {
            return "rgba(251, 191, 36, 0.1)";
        } else {
            return "rgba(239, 68, 68, 0.1)";
        }
    };

    return (
        <Box
            sx={{
                marginTop: "12px",
                backgroundColor: getBackgroundColor(),
                borderRadius: "8px",
                padding: "14px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
            }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontSize: "12px", color: theme.secondaryTextColor }}>
                    Progresso Anual
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Typography
                        sx={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color: barColor
                        }}
                    >
                        {progressPercentage.toFixed(1)}%
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                        <Typography sx={{ fontSize: "12px", color: barColor }}>
                            {deviationPercentage > 0 ? "▲" : "▼"}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: barColor }}>
                            {Math.abs(deviationPercentage).toFixed(1)}%
                        </Typography>
                    </Box>
                </Box>
            </Box>
            <Box sx={{ position: "relative", width: "100%" }}>
                <LinearProgress
                    variant="determinate"
                    value={Math.min(progressPercentage, 100)}
                    sx={{
                        height: 6,
                        borderRadius: 4,
                        backgroundColor: "rgba(120, 165, 204, 0.1)",
                        "& .MuiLinearProgress-bar": {
                            backgroundColor: barColor,
                            borderRadius: 4,
                        },
                    }}
                />
                {/* Marcador de meta esperada */}
                <Box
                    sx={{
                        position: "absolute",
                        top: -2,
                        left: `${Math.min(expectedProgressPercentage, 100)}%`,
                        transform: "translateX(-50%)",
                        width: 2,
                        height: 10,
                        backgroundColor: theme.primaryTextColor || "#9CA3AF",
                        borderRadius: "2px",
                        zIndex: 1,
                    }}
                />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "4px" }}>
                <Typography sx={{ fontSize: "10px", color: theme.secondaryTextColor }}>
                    Meta esperada: {expectedProgressPercentage.toFixed(1)}%
                </Typography>
            </Box>
        </Box>
    );
}
